import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Plus, Search, Edit3, AlertCircle, Users, Crown, MapPin, Phone, TrendingUp, Power, PowerOff, Check, X, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI, handleApiError } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmDialog from '../components/ConfirmDialog';
import { useFlashMessage } from '../components/FlashMessage';
import AdvancedPagination from '../components/AdvancedPagination';
import { FormSelect } from '../components/FormSelect';

const SystemAdminLocationManagement = () => {
  const { isSystemAdmin } = useAuth();
  const [locations, setLocations] = useState([]);
  const [patrons, setPatrons] = useState([]);
  const [filters, setFilters] = useState({
    searchTerm: '',
    filterType: 'all', // all, valet, wash, both
    statusFilter: 'all' // all, active, inactive
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Server-side pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limitPerPage, setLimitPerPage] = useState(20);

  // Stats state
  const [stats, setStats] = useState({
    totalLocations: 0,
    activeLocations: 0,
    locationsWithOwners: 0,
    totalRevenue: 0
  });

  // Dialog ve Flash mesaj state'leri
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [selectedPatronIds, setSelectedPatronIds] = useState([]);
  const [removeOwnerDialogOpen, setRemoveOwnerDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [locationToToggleStatus, setLocationToToggleStatus] = useState(null);
  const [removeOwnerLoading, setRemoveOwnerLoading] = useState(false);
  const [ownerToRemove, setOwnerToRemove] = useState(null);
  const { showSuccess, showDanger, FlashContainer } = useFlashMessage();

  // Load all data once (client-side filtering approach)
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const [locationsResponse, usersResponse, statsResponse] = await Promise.all([
        adminAPI.getLocations('all', 1, 9999), // Get all locations
        adminAPI.getUsers(),
        adminAPI.getStats()
      ]);

      if (locationsResponse.data.success) {
        const locations = locationsResponse.data.data;
        setLocations(locations);
      }

      if (usersResponse.data.success) {
        const allUsers = usersResponse.data.data;
        const patronList = allUsers.filter(user => user.user_type === 'patron');
        setPatrons(patronList);
      }

      if (statsResponse.data.success) {
        setStats(statsResponse.data.data);
      }

    } catch (err) {
      console.error('Failed to load data:', err);
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  }, []);


  // Load data on mount
  useEffect(() => {
    if (!isSystemAdmin()) return;
    loadData();
  }, [loadData, isSystemAdmin]);

  // Client-side filtering (like UserManagement.js)
  const filteredLocations = locations.filter(location => {
    const matchesSearch = location.name.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
                         (location.address && location.address.toLowerCase().includes(filters.searchTerm.toLowerCase()));

    const matchesType = filters.filterType === 'all' || location.location_type === filters.filterType;

    const matchesStatus = filters.statusFilter === 'all' ||
                         (filters.statusFilter === 'active' && location.is_active) ||
                         (filters.statusFilter === 'inactive' && !location.is_active);

    return matchesSearch && matchesType && matchesStatus;
  });

  // Client-side pagination
  const startIndex = (currentPage - 1) * limitPerPage;
  const endIndex = startIndex + limitPerPage;
  const paginatedLocations = filteredLocations.slice(startIndex, endIndex);
  const calculatedTotalPages = Math.ceil(filteredLocations.length / limitPerPage);

  // Update totalCount and totalPages for pagination component
  useEffect(() => {
    setTotalCount(filteredLocations.length);
    setTotalPages(calculatedTotalPages);
  }, [filteredLocations.length, calculatedTotalPages]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.searchTerm, filters.filterType, filters.statusFilter]);

  // Pagination handlers
  const changePage = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
    }
  }, [currentPage, totalPages]);

  const handleLimitChange = useCallback((newLimit) => {
    setLimitPerPage(newLimit);
    setCurrentPage(1);
  }, []);


  const handleToggleStatusClick = (location) => {
    setLocationToToggleStatus(location);
    setStatusDialogOpen(true);
  };

  const handleStatusConfirm = async () => {
    if (!locationToToggleStatus) return;

    setStatusLoading(true);
    try {
      const newStatus = locationToToggleStatus.is_active ? 'inactive' : 'active';

      await adminAPI.updateLocationStatus(locationToToggleStatus.id, newStatus);

      const statusText = newStatus === 'active' ? 'aktifleştirildi' : 'pasifleştirildi';
      showSuccess(`${locationToToggleStatus.name} başarıyla ${statusText}.`);

      setStatusDialogOpen(false);
      setLocationToToggleStatus(null);
      await loadData();
    } catch (err) {
      console.error('Status toggle error:', err);
      showDanger(`Status güncellenirken hata oluştu: ${handleApiError(err)}`);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleStatusCancel = () => {
    setStatusDialogOpen(false);
    setLocationToToggleStatus(null);
    setStatusLoading(false);
  };

  const handleDeleteLocationClick = (location) => {
    setLocationToDelete(location);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!locationToDelete) return;

    setDeleteLoading(true);
    try {
      await adminAPI.deleteLocation(locationToDelete.id);
      showSuccess(`${locationToDelete.name} başarıyla silindi.`);

      setDeleteDialogOpen(false);
      setLocationToDelete(null);
      await loadData();
    } catch (err) {
      console.error('Delete location error:', err);
      showDanger(`Lokasyon silinirken hata oluştu: ${handleApiError(err)}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setLocationToDelete(null);
    setDeleteLoading(false);
  };

  const handleAssignCancel = () => {
    setAssignDialogOpen(false);
    setSelectedLocation(null);
    setSelectedPatronIds([]);
    setAssignLoading(false);
  };

  const handleRemoveOwnerClick = (location, owner) => {
    setOwnerToRemove({ location, owner });
    setRemoveOwnerDialogOpen(true);
  };

  const handleRemoveOwnerConfirm = async () => {
    if (!ownerToRemove) return;

    setRemoveOwnerLoading(true);
    try {
      await adminAPI.removeLocationOwner(ownerToRemove.location.id, ownerToRemove.owner.id);

      showSuccess(`${ownerToRemove.owner.full_name} artık ${ownerToRemove.location.name} lokasyonunun sahibi değil.`);

      setRemoveOwnerDialogOpen(false);
      setOwnerToRemove(null);
      await loadData();
    } catch (err) {
      showDanger(`Sahip kaldırılırken hata oluştu: ${handleApiError(err)}`);
    } finally {
      setRemoveOwnerLoading(false);
    }
  };

  const handleRemoveOwnerCancel = () => {
    setRemoveOwnerDialogOpen(false);
    setOwnerToRemove(null);
    setRemoveOwnerLoading(false);
  };

  const handlePatronToggle = (patronId) => {
    setSelectedPatronIds(prev =>
      prev.includes(patronId)
        ? prev.filter(id => id !== patronId)
        : [...prev, patronId]
    );
  };

  const handleAssignConfirm = async () => {
    if (selectedPatronIds.length === 0) {
      showDanger('En az bir patron seçmelisiniz.');
      return;
    }

    setAssignLoading(true);
    try {
      // Her seçili patron için atama yap
      for (const patronId of selectedPatronIds) {
        await adminAPI.assignLocationOwner(selectedLocation.id, patronId);
      }

      await loadData();
      setAssignDialogOpen(false);
      setSelectedLocation(null);
      setSelectedPatronIds([]);

      const patronNames = selectedPatronIds.map(id => {
        const patron = patrons.find(p => p.id === id);
        return patron?.full_name;
      }).filter(Boolean).join(', ');

      showSuccess(`${selectedLocation.name} lokasyonu ${patronNames} isimli patron(lar)a başarıyla atandı.`);
    } catch (err) {
      console.error('Sahip atama hatası:', err);
      showDanger(handleApiError(err));
    } finally {
      setAssignLoading(false);
    }
  };

  const getLocationTypeText = (type) => {
    switch (type) {
      case 'both': return 'Vale + Oto Yıkama';
      case 'wash': return 'Oto Yıkama';
      case 'valet': return 'Vale';
      default: return type;
    }
  };

  const getLocationTypeBadge = (type) => {
    const colors = {
      valet: 'bg-red-50 text-[#7a2c2c]',
      wash: 'bg-green-100 text-green-800',
      both: 'bg-purple-100 text-purple-800'
    };

    return (
      <span className={`px-2 py-1 text-xs rounded-full ${colors[type] || 'bg-gray-100 text-gray-800'}`}>
        {getLocationTypeText(type)}
      </span>
    );
  };


  if (!isSystemAdmin()) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
          <p className="text-red-700">Bu sayfaya erişim yetkiniz yok.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lokasyon Yönetimi</h1>
          <p className="text-gray-600 mt-1">Sistem genelindeki tüm lokasyonları yönetin</p>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <Link to="/admin/users/new" className="btn-secondary flex items-center justify-center">
            <Users className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Yeni </span>Kullanıcı
          </Link>
          <Link to="/admin/locations/new" className="btn-primary flex items-center justify-center">
            <Plus className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Yeni </span>Lokasyon
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 md:gap-3 lg:gap-4">
        <div className="card">
          <div className="card-body text-center py-3 lg:py-4">
            <Building2 className="h-5 w-5 lg:h-8 lg:w-8 text-[#7a2c2c] mx-auto mb-1 lg:mb-2" />
            <p className="text-lg lg:text-2xl font-bold text-gray-900">{stats.totalLocations}</p>
            <p className="text-xs lg:text-sm text-gray-600">Toplam Lokasyon</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center py-3 lg:py-4">
            <Users className="h-5 w-5 lg:h-8 lg:w-8 text-green-500 mx-auto mb-1 lg:mb-2" />
            <p className="text-lg lg:text-2xl font-bold text-gray-900">{stats.activeLocations}</p>
            <p className="text-xs lg:text-sm text-gray-600">Aktif Lokasyon</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center py-3 lg:py-4">
            <Crown className="h-5 w-5 lg:h-8 lg:w-8 text-yellow-500 mx-auto mb-1 lg:mb-2" />
            <p className="text-lg lg:text-2xl font-bold text-gray-900">
              {stats.locationsWithOwners || 0}
            </p>
            <p className="text-xs lg:text-sm text-gray-600">Sahipli Lokasyon</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="relative flex-1 sm:flex-none sm:w-64">
                <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Lokasyon ara..."
                  value={filters.searchTerm}
                  onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                  className="form-input pl-10"
                />
              </div>
              <div className="flex gap-3">
                <FormSelect
                  name="filterType"
                  value={filters.filterType}
                  onChange={(e) => setFilters(prev => ({ ...prev, filterType: e.target.value }))}
                  options={[
                    { value: 'all', label: 'Tüm Tipler' },
                    { value: 'valet', label: 'Vale' },
                    { value: 'wash', label: 'Oto Yıkama' },
                    { value: 'both', label: 'Vale + Oto Yıkama' }
                  ]}
                  className="flex-1 sm:flex-none text-sm"
                />
                <FormSelect
                  name="statusFilter"
                  value={filters.statusFilter}
                  onChange={(e) => setFilters(prev => ({ ...prev, statusFilter: e.target.value }))}
                  options={[
                    { value: 'all', label: 'Tüm Durumlar' },
                    { value: 'active', label: 'Aktif' },
                    { value: 'inactive', label: 'Pasif' }
                  ]}
                  className="flex-1 sm:flex-none text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Locations List */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Lokasyonlar</h3>
        </div>
        <div className="card-body p-0">
          {paginatedLocations.length > 0 ? (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lokasyon
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tip
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sahip
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Personel
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ciro
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Durum
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İşlemler
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedLocations.map((location) => (
                    <tr
                      key={location.id}
                      className={`hover:bg-gray-50 ${
                        !location.is_active ? 'bg-gray-50 opacity-75' : ''
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 bg-red-50 rounded-full flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-[#7a2c2c]" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {location.name}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <MapPin className="h-4 w-4 mr-1" />
                              {location.city}, {location.district}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getLocationTypeBadge(location.type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {location.owners && location.owners.length > 0 ? (
                          <div className="space-y-1">
                            {location.owners.map((owner, index) => (
                              <div key={owner.id} className="flex items-center justify-between text-sm">
                                <div className="flex items-center">
                                  <Crown className="h-4 w-4 mr-2 text-yellow-500" />
                                  <div>
                                    <div className="font-medium text-gray-900">{owner.full_name}</div>
                                    <div className="text-gray-500 flex items-center">
                                      <Phone className="h-4 w-4 mr-1" />
                                      {owner.phone}
                                    </div>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleRemoveOwnerClick(location, owner)}
                                  className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50"
                                  title="Sahibi Kaldır"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={() => {
                                setSelectedLocation(location);
                                setAssignDialogOpen(true);
                              }}
                              className="text-[#7a2c2c] hover:text-[#5a1f1f] text-xs font-medium mt-1"
                            >
                              + Sahip Ekle
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setSelectedLocation(location);
                              setAssignDialogOpen(true);
                            }}
                            className="text-[#7a2c2c] hover:text-[#5a1f1f] text-sm font-medium"
                          >
                            Sahip Ata
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <Users className="h-4 w-4 mr-2 text-gray-400" />
                          <span className="font-medium">{location.active_today || 0}</span>
                          <span className="text-gray-500 ml-1">/ {location.user_count || 0}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <TrendingUp className="h-4 w-4 mr-2 text-green-500" />
                          ₺{parseFloat(location.revenue || 0).toLocaleString('tr-TR')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          location.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {location.is_active ? 'Aktif' : 'Pasif'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleToggleStatusClick(location)}
                            className={`${
                              location.is_active
                                ? 'text-red-600 hover:text-red-900'
                                : 'text-green-600 hover:text-green-900'
                            }`}
                            title={location.is_active ? 'Pasifleştir' : 'Aktifleştir'}
                          >
                            {location.is_active ? (
                              <PowerOff className="h-4 w-4" />
                            ) : (
                              <Power className="h-4 w-4" />
                            )}
                          </button>
                          <Link
                            to={`/admin/locations/edit/${location.id}`}
                            className="text-[#7a2c2c] hover:text-[#5a1f1f]"
                            title="Düzenle"
                          >
                            <Edit3 className="h-4 w-4" />
                          </Link>
                          <button
                            onClick={() => handleDeleteLocationClick(location)}
                            className="text-red-600 hover:text-red-900"
                            title="Sil"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-3 p-4">
                {paginatedLocations.map((location) => (
                  <div key={location.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 truncate">{location.name}</h4>
                        <p className="text-sm text-gray-500 truncate">{location.city} / {location.district}</p>
                      </div>
                      <div className="flex items-center space-x-3 ml-2">
                        <button
                          onClick={() => handleToggleStatusClick(location)}
                          className={`p-2 rounded-full ${
                            location.is_active
                              ? 'bg-red-50 text-red-600 hover:bg-red-100'
                              : 'bg-green-50 text-green-600 hover:bg-green-100'
                          }`}
                          title={location.is_active ? 'Pasifleştir' : 'Aktifleştir'}
                        >
                          {location.is_active ? (
                            <PowerOff className="h-4 w-4" />
                          ) : (
                            <Power className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteLocationClick(location)}
                          className="p-2 rounded-full bg-red-50 text-red-600 hover:bg-red-100"
                          title="Sil"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <Link
                          to={`/admin/locations/edit/${location.id}`}
                          className="p-2 rounded-full bg-red-50 text-[#7a2c2c] hover:bg-red-100"
                          title="Düzenle"
                        >
                          <Edit3 className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>

                    {/* Status and Type */}
                    <div className="flex items-center gap-2 mb-3">
                      {getLocationTypeBadge(location.type)}
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        location.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {location.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </div>

                    {/* Quick Stats */}
                    <div className="flex justify-between text-sm mb-3">
                      <div className="flex items-center">
                        <Users className="h-4 w-4 mr-1 text-gray-400" />
                        <span>{location.active_today || 0}/{location.user_count || 0}</span>
                      </div>
                      <div className="flex items-center">
                        <TrendingUp className="h-4 w-4 mr-1 text-green-500" />
                        <span className="font-medium">₺{parseFloat(location.revenue || 0).toLocaleString('tr-TR')}</span>
                      </div>
                    </div>

                    {/* Owners */}
                    {location.owners && location.owners.length > 0 ? (
                      <div className="space-y-2">
                        {location.owners.map((owner) => (
                          <div key={owner.id} className="flex items-center justify-between text-sm">
                            <div className="flex items-center">
                              <Crown className="h-4 w-4 mr-2 text-yellow-500" />
                              <span className="font-medium">{owner.full_name}</span>
                            </div>
                            <button
                              onClick={() => handleRemoveOwnerClick(location, owner)}
                              className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50"
                              title="Sahibi Kaldır"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => {
                            setSelectedLocation(location);
                            setAssignDialogOpen(true);
                          }}
                          className="w-full py-1 px-2 bg-red-50 text-[#7a2c2c] rounded text-xs font-medium hover:bg-red-100"
                        >
                          + Sahip Ekle
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setSelectedLocation(location);
                          setAssignDialogOpen(true);
                        }}
                        className="w-full py-2 px-3 bg-red-50 text-[#7a2c2c] rounded-lg text-sm font-medium hover:bg-red-100"
                      >
                        Sahip Ata
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Advanced Pagination Component */}
              <AdvancedPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                limitPerPage={limitPerPage}
                onPageChange={changePage}
                onLimitChange={handleLimitChange}
              />
            </>
          ) : (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Lokasyon bulunamadı</p>
              <p className="text-gray-400 text-sm">
                {filters.searchTerm || filters.filterType !== 'all'
                  ? 'Arama kriterlerinizi değiştirmeyi deneyin'
                  : 'Henüz hiç lokasyon kaydı yapılmamış'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Assign Owner Confirmation Dialog */}
      <ConfirmDialog
        isOpen={assignDialogOpen}
        onClose={handleAssignCancel}
        onConfirm={handleAssignConfirm}
        title="Lokasyon Sahibi Ata"
        confirmText="Seçili Patronları Ata"
        cancelText="İptal"
        type="info"
        loading={assignLoading}
      >
        {selectedLocation && (
          <div className="space-y-4">
            <p className="text-gray-600 text-sm">
              <strong>{selectedLocation.name}</strong> lokasyonuna sahip olacak patronları seçin:
            </p>

            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
              {patrons.filter(p => p.is_active).length > 0 ? (
                patrons.filter(p => p.is_active).map((patron) => (
                  <label
                    key={patron.id}
                    className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={selectedPatronIds.includes(patron.id)}
                      onChange={() => handlePatronToggle(patron.id)}
                      className="mr-3 h-4 w-4 text-[#7a2c2c] focus:ring-[#7a2c2c] border-gray-300 rounded"
                    />
                    <div className="flex items-center flex-1">
                      <Crown className="h-5 w-5 text-yellow-500 mr-3" />
                      <div>
                        <div className="font-medium text-gray-900">{patron.full_name}</div>
                        <div className="text-sm text-gray-500">{patron.email}</div>
                      </div>
                    </div>
                    {selectedPatronIds.includes(patron.id) && (
                      <Check className="h-5 w-5 text-green-500" />
                    )}
                  </label>
                ))
              ) : (
                <div className="text-center py-8">
                  <Crown className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">Aktif patron bulunamadı</p>
                </div>
              )}
            </div>

            {selectedPatronIds.length > 0 && (
              <p className="text-sm text-[#7a2c2c]">
                {selectedPatronIds.length} patron seçili
              </p>
            )}
          </div>
        )}
      </ConfirmDialog>

      {/* Remove Owner Confirmation Dialog */}
      <ConfirmDialog
        isOpen={removeOwnerDialogOpen}
        onClose={handleRemoveOwnerCancel}
        onConfirm={handleRemoveOwnerConfirm}
        title="Sahibi Kaldır"
        message={
          ownerToRemove ?
            `"${ownerToRemove.owner.full_name}" kullanıcısını "${ownerToRemove.location.name}" lokasyonunun sahipliğinden kaldırmak istediğinizden emin misiniz?` :
            ''
        }
        confirmText="Kaldır"
        cancelText="İptal"
        type="danger"
        loading={removeOwnerLoading}
      />

      {/* Status Toggle Confirmation Dialog */}
      <ConfirmDialog
        isOpen={statusDialogOpen}
        onClose={handleStatusCancel}
        onConfirm={handleStatusConfirm}
        title="Lokasyon Durumu Değiştir"
        message={`${locationToToggleStatus?.name || 'Bu lokasyon'}u ${locationToToggleStatus?.is_active ? 'pasifleştirmek' : 'aktifleştirmek'} istediğinizden emin misiniz?`}
        confirmText={locationToToggleStatus?.is_active ? 'Pasifleştir' : 'Aktifleştir'}
        cancelText="İptal"
        type={locationToToggleStatus?.is_active ? 'warning' : 'info'}
        loading={statusLoading}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Lokasyon Sil"
        message={`${locationToDelete?.name || 'Bu lokasyon'}u kalıcı olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz ve tüm veriler kaybolacaktır.`}
        confirmText="Sil"
        cancelText="İptal"
        type="danger"
        loading={deleteLoading}
      />

      {/* Flash Messages */}
      <FlashContainer />
    </div>
  );
};

export default SystemAdminLocationManagement;